import nodePool from '@ps/bridge/qc/node-pool'
import { scripts } from '@ps/bridge/qc/scripts'
import {
  findChildByRouteIndexInScene,
  getRouteIndexByNodeInScene,
} from '@ps/utils/game'
import qc from 'qc'
import { IvSyncStatus } from './enums'
import store from '@ps/store'
import gameIframe from '@ps/bridge/iframe/iframe'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { EditorDataScript } from '@ps/bridge/qc/scripts/editor'
import { deepReplaceAstUUID } from '../common/utils'
import { getTimelineBySceneUuid, setTimeline } from '../timeline/timeline'
import _ from 'lodash'
import { MessageBox } from 'element-ui'
import { executeAction } from '@ps/workbench/undo-redo'
import i18n from '@ps/lang'
import { IV_HOTAREA_GROUP_NAME } from './brain'
import { IvTrackType } from '../timeline/enums'
import { EventBus, Event } from '@ps/utils/event-bus'
import { updateByVideoSegment } from '../timeline/utiles/segment'
import { useTrackGroupInsertResultsByDiff } from '../timeline/utiles/track-group-insert'
import { ivTimeline2NodeExecuteOperation } from '../timeline/action'
import { useRemovedSegments } from '../timeline/utiles/track-removed-segments'

function replaceUuidBetweenScene(uuid: string, sceneNode: qc.Node) {
  const node = nodePool.find(uuid)
  const path = getRouteIndexByNodeInScene(node)
  const newNode = findChildByRouteIndexInScene(sceneNode, path)
  return newNode.uuid
}

function getVideoUrlByNodeUuid(videoNodeUuid: string): string {
  if (_.isEmpty(videoNodeUuid)) {
    return ''
  }
  const videoNode = nodePool.find(videoNodeUuid)
  if (!videoNode) return ''
  const videoComponent = videoNode.getScript(scripts.video.name)
  const videoUrl = videoComponent.sourceVideoUrl
  return videoUrl
}

export function getSyncStatusScene(sceneNode: qc.Node): IvSyncStatus {
  const brainData = store.state.ivBrain.brainData
  const brainItem = brainData.find((item) => item.sceneUuid === sceneNode.uuid)
  return brainItem.sync
}

export function sceneHasVideo(sceneNode: qc.Node): boolean {
  const brainData = store.state.ivBrain.brainData
  const brainItem = brainData.find((item) => item.sceneUuid === sceneNode.uuid)
  if (!brainItem) {
    return false
  }
  return !_.isEmpty(brainItem.videoUuid)
}

export function setSyncStatusScene(
  sceneNode: qc.Node,
  syncStatus: IvSyncStatus,
) {
  const brainData = store.state.ivBrain.brainData
  const brainItem = brainData.find((item) => item.sceneUuid === sceneNode.uuid)
  brainItem.sync = syncStatus
  EventBus.$emit(Event.IV_BRAIN_CHANGE)
}

export function getSameVideoScene(
  sceneNode: qc.Node,
  syncStatusList: IvSyncStatus[],
  getOther = true,
  oriVideoUrl = '',
): qc.Node[] {
  const brainData = store.state.ivBrain.brainData
  const brainItem = brainData.find((item) => item.sceneUuid === sceneNode.uuid)
  if (!brainItem) return []
  let videoUrl: string
  if (!_.isEmpty(oriVideoUrl)) {
    videoUrl = oriVideoUrl
  } else {
    videoUrl = getVideoUrlByNodeUuid(brainItem.videoUuid)
  }
  if (_.isEmpty(videoUrl)) {
    return []
  }
  const res: qc.Node[] = []
  brainData.forEach((item) => {
    const targetVideoUrl = getVideoUrlByNodeUuid(item.videoUuid)
    if (getOther) {
      if (
        syncStatusList.indexOf(item.sync) > -1 &&
        targetVideoUrl === videoUrl &&
        item.sceneUuid !== sceneNode.uuid
      ) {
        res.push(nodePool.find(item.sceneUuid))
      }
    } else {
      if (
        syncStatusList.indexOf(item.sync) > -1 &&
        targetVideoUrl === videoUrl
      ) {
        res.push(nodePool.find(item.sceneUuid))
      }
    }
  })
  return res
}

export function syncSceneBySameVideo(fromScene: qc.Node, toScene: qc.Node) {
  const qcGame = gameIframe.qcGame
  const res = {
    nodes: [],
    ast: null,
    newNodes: [],
    newAst: null,
  }
  toScene.children.forEach((child) => {
    res.nodes.push(child)
  })
  // 节点克隆
  toScene.removeChildren()
  fromScene.children.forEach((childNode) => {
    qcGame.add.clone(childNode, toScene)
  })
  // 原场景和目标场景的跳转热区数量不相同的处理逻辑
  const brainData = store.state.ivBrain.brainData
  const toIndex = brainData.findIndex((item) => item.sceneUuid === toScene.uuid)
  const fromIndex = brainData.findIndex(
    (item) => item.sceneUuid === fromScene.uuid,
  )
  const fromActionNum = brainData[fromIndex].actions.length
  const toActionNum = brainData[toIndex].actions.length
  const nodesToDelete = []
  const nodesToRevert = []
  const linkNodeToRevert = brainData[toIndex].link.id
  for (let i = 0; i < toActionNum; i++) {
    // eslint-disable-next-line no-continue
    if (!brainData[toIndex].actions[i].nodeUuid) continue
    const uuid = brainData[toIndex].actions[i].nodeUuid
    nodesToRevert.push(uuid)
  }

  // 时间轴数据转换
  let timelineData = getTimelineBySceneUuid(fromScene.uuid)
  const toTimelineData = getTimelineBySceneUuid(toScene.uuid)
  timelineData.forEach((timelineItem) => {
    timelineItem.segments.forEach((item) => {
      item.id = replaceUuidBetweenScene(item.id, toScene)
      item.groupId = replaceUuidBetweenScene(item.groupId, toScene)
      item.sceneUuid = toScene.uuid
    })
  })
  // 时间轴里用目标场景的场景间的互动元件
  const toIvTrack = toTimelineData.find((item) => item.type === IvTrackType.Iv)
  if (toIvTrack) {
    const toSegmentEnd = toIvTrack.segments.find((item) => item.isEnd === true)
    const fromVideoTrack = timelineData.find(
      (item) => item.type === IvTrackType.Video,
    )
    const toVideoTrack = toTimelineData.find(
      (item) => item.type === IvTrackType.Video,
    )
    const fromVideoFrames = fromVideoTrack.segments[0].frames
    const toVideoFrames = toVideoTrack.segments[0].frames
    const delFrames = fromVideoFrames - toVideoFrames
    const fromIvTrack = timelineData.find(
      (item) => item.type === IvTrackType.Iv,
    )
    if (fromIvTrack) {
      // 原场景有互动轨道，目标场景也有的情况
      const oriFromSegmentEnd = fromIvTrack.segments.find(
        (item) => item.isEnd === true,
      )
      // 上面已经对from的时间轴相关id进行替换，这里先暂存，下面toSegmentEnd要用这里的值
      const oriToSegmentEndGroupId = oriFromSegmentEnd.groupId
      // 先删除场景间的互动元件segment
      fromIvTrack.segments = fromIvTrack.segments.filter(
        (item) => item.isEnd !== true,
      )
      if (toSegmentEnd) {
        // 目标场景间互动元件的start和frames适配原场景的时长
        toSegmentEnd.start += delFrames
        toSegmentEnd.start = toSegmentEnd.start < 0 ? 0 : toSegmentEnd.start
        toSegmentEnd.frames =
          toSegmentEnd.frames > fromVideoFrames
            ? fromVideoFrames
            : toSegmentEnd.frames
        toSegmentEnd.groupId = oriToSegmentEndGroupId
        // end元件的trackId用回原场景的trackId
        toSegmentEnd.trackId = oriFromSegmentEnd.trackId
        fromIvTrack.segments.push(_.cloneDeep(toSegmentEnd))
      }
    } else {
      // 原场景没有互动轨道，目标场景有的情况
      if (toSegmentEnd) {
        // 目标场景间互动元件的start和frames适配原场景的时长
        toSegmentEnd.start += delFrames
        toSegmentEnd.start = toSegmentEnd.start < 0 ? 0 : toSegmentEnd.start
        toSegmentEnd.frames =
          toSegmentEnd.frames > fromVideoFrames
            ? fromVideoFrames
            : toSegmentEnd.frames
        toSegmentEnd.groupId = toScene.getChild(IV_HOTAREA_GROUP_NAME).uuid
      }
      timelineData.unshift(toIvTrack)
    }
  } else {
    // 目标场景没有互动轨道的情况
    timelineData = timelineData.filter((item) => item.type !== IvTrackType.Iv)
  }
  // 删除元件
  const oldTimelineData = _.cloneDeep(timelineData)
  const removedSegments = useRemovedSegments(
    toScene.uuid,
    () => {
      updateByVideoSegment(toScene.uuid, timelineData)
    },
    {
      fromTracks: timelineData,
    },
  )
  const removeNodesWhenTimelineMerge = removedSegments
    .map((uuid) => nodePool.find(uuid))
    .filter((node) => Boolean(node))
  // 目标场景合并时间轴时要删除的节点对应的原场景里的节点uuid，用于后面事件数据处理前先删除原场景中对应的事件
  const removeNodesUuidFromSceneWhenTimelineMerge =
    removeNodesWhenTimelineMerge.map((item) => {
      const uuid = replaceUuidBetweenScene(item.uuid, fromScene)
      return uuid
    })
  removeNodesWhenTimelineMerge.forEach((node) => {
    node.removeSelf()
  })
  // 调整层级
  const groupInsertResults = useTrackGroupInsertResultsByDiff(
    toScene.uuid,
    timelineData,
    oldTimelineData,
  )
  ivTimeline2NodeExecuteOperation(groupInsertResults)
  setTimeline(toScene.uuid, timelineData)
  // 脑图数据转换
  if (toIndex > -1) {
    // brainData[toIndex].link.type = brainData[fromIndex].link.type
    // brainData[toIndex].link.id = replaceUuidBetweenScene(
    //   brainData[fromIndex].link.id,
    //   toScene,
    // )
    // brainData[toIndex].link.interactiveType =
    //   brainData[fromIndex].link.interactiveType
    brainData[toIndex].videoUuid = replaceUuidBetweenScene(
      brainData[fromIndex].videoUuid,
      toScene,
    )
    brainData[toIndex].sync = IvSyncStatus.Sync
    brainData[fromIndex].sync = IvSyncStatus.Sync
    store.commit('ivBrain/updateBrainData', _.cloneDeep(brainData))
  }
  // 事件数据转换
  const fromScenePed = editorScript.getScript(fromScene) as EditorDataScript
  const toScenePed = editorScript.getScript(toScene) as EditorDataScript
  res.ast = _.cloneDeep(toScenePed.vpAst)
  let eventsToRevert = []
  toScenePed.vpAst = [...Object.values(toScenePed.vpAst)]
  // 找回目标场景的跳转热区对应的事件，后面补回
  if (nodesToRevert.length > 0) {
    eventsToRevert = _.cloneDeep(
      toScenePed.vpAst.filter(
        (item) => nodesToRevert.indexOf(item.condition[0].node) > -1,
      ),
    )
  }
  toScenePed.vpAst = _.cloneDeep(fromScenePed.vpAst)
  toScenePed.vpAst = [...Object.values(toScenePed.vpAst)]
  // 目标场景合并时间轴时删掉的节点对应的原场景的节点对应的事件需要删掉
  toScenePed.vpAst = toScenePed.vpAst.filter(
    (item) =>
      item.condition &&
      item.condition.length > 0 &&
      removeNodesUuidFromSceneWhenTimelineMerge.indexOf(
        item.condition[0].node,
      ) === -1,
  )
  toScenePed.vpAst.forEach((eventItem) => {
    eventItem.action = eventItem.action.filter(
      (item) =>
        removeNodesUuidFromSceneWhenTimelineMerge.indexOf(item.node) === -1,
    )
  })
  deepReplaceAstUUID({ vpAst: toScenePed.vpAst, sceneNode: toScene })
  toScenePed.vpAst = [...Object.values(toScenePed.vpAst)]
  // 删除原场景复制过来的跳转热区节点
  for (let i = 0; i < fromActionNum; i++) {
    // eslint-disable-next-line no-continue
    if (!brainData[fromIndex].actions[i].nodeUuid) continue
    const uuid = replaceUuidBetweenScene(
      brainData[fromIndex].actions[i].nodeUuid,
      toScene,
    )
    nodesToDelete.push(uuid)
  }
  const linkNodeToDelete = replaceUuidBetweenScene(
    brainData[fromIndex].link.id,
    toScene,
  )
  // 删掉原场景的热区及其跳转事件
  if (nodesToDelete.length > 0) {
    nodesToDelete.forEach((uuid) => {
      nodePool.find(uuid).removeSelf()
    })
    toScenePed.vpAst = toScenePed.vpAst.filter(
      (item) => nodesToDelete.indexOf(item.condition[0].node) === -1,
    )
  }
  // 删除原场景复制过来的link node
  if (linkNodeToDelete) {
    nodePool.find(linkNodeToDelete).removeSelf()
  }
  // 还原场景间跳转的辅助节点link node
  if (linkNodeToRevert) {
    toScene.addChild(nodePool.find(linkNodeToRevert))
  }
  // 补回目标场景的热区跳转事件
  if (eventsToRevert.length > 0) {
    toScenePed.vpAst.push(...eventsToRevert)
  }
  res.newAst = _.cloneDeep(toScenePed.vpAst)
  // 还原原来的场景间跳转的热区节点
  if (nodesToRevert.length > 0) {
    nodesToRevert.forEach((uuid) => {
      toScene.getChild(IV_HOTAREA_GROUP_NAME).addChild(nodePool.find(uuid))
    })
  }
  toScene.children.forEach((child) => {
    res.newNodes.push(child)
  })
  return res
}

function getSyncSceneAciton(value) {
  return {
    // eslint-disable-next-line
    execute: ({ newValue }) => {
      const fromScene = newValue.fromScene
      const toScenes = newValue.toScenes
      const nodesToUndo = {}
      const asts = {}
      toScenes.forEach((toScene: qc.Node) => {
        const { nodes, ast } = syncSceneBySameVideo(fromScene, toScene)
        nodesToUndo[toScene.uuid] = nodes
        asts[toScene.uuid] = ast
      })
      return {
        nodes: nodesToUndo,
        asts,
      }
    },
    // eslint-disable-next-line
    undo: ({ oldValue }, response: { nodes, asts }) => {
      const asts = response.asts
      const nodes = response.nodes
      for (const i in nodes) {
        const sceneNode = nodePool.find(i)
        sceneNode.removeChildren()
        nodes[i].forEach((node) => {
          sceneNode.addChild(node)
        })
      }
      for (const i in asts) {
        const sceneNode = nodePool.find(i)
        const ped = editorScript.getScript(sceneNode) as EditorDataScript
        ped.vpAst = _.cloneDeep(asts[i])
      }
    },
    value,
    async: true,
    methodName: 'getSyncSceneAciton',
  }
}

export function syncSceneAciton(fromScene: qc.Node, toScenes: qc.Node[]) {
  executeAction({
    action: getSyncSceneAciton({
      newValue: { fromScene, toScenes },
      oldValue: { fromScene, toScenes },
    }),
    record: true,
  })
}

export async function suffixSync() {
  const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
  if (sceneHasVideo(currentSceneNode) && currentSceneNode.hasEditThisTime) {
    const syncStatus = getSyncStatusScene(currentSceneNode)
    if (syncStatus === IvSyncStatus.Sync) {
      const sceneNodes = getSameVideoScene(currentSceneNode, [
        IvSyncStatus.Sync,
      ])
      if (sceneNodes.length > 0) {
        syncSceneAciton(currentSceneNode, sceneNodes)
      }
    } else if (syncStatus === IvSyncStatus.None) {
      const sceneNodes = getSameVideoScene(currentSceneNode, [
        IvSyncStatus.None,
      ])
      if (sceneNodes.length > 0) {
        await MessageBox.confirm(
          i18n.t('interactiveVideo.iv.sync.content'),
          i18n.t('interactiveVideo.iv.sync.title'),
          {
            confirmButtonText: i18n.t('interactiveVideo.iv.sync.sync'),
            cancelButtonText: i18n.t('interactiveVideo.iv.sync.noSync'),
            type: 'warning',
            customClass: 'pt-custom-confirm-message',
            center: true,
          },
        )
          .then(() => {
            console.log(11111)
            setSyncStatusScene(currentSceneNode, IvSyncStatus.Sync)
            syncSceneAciton(currentSceneNode, sceneNodes)
            store.dispatch('ivTimeline/refreshTimeline')
          })
          .catch(() => {
            console.log(22222)
            setSyncStatusScene(currentSceneNode, IvSyncStatus.noSync)
            sceneNodes.forEach((sceneNode: qc.Node) => {
              setSyncStatusScene(sceneNode, IvSyncStatus.noSync)
            })
          })
      }
    }
  }
}
